import _ from 'lodash';
import { ToastrService } from 'ngx-toastr';
import { Container, Graphics, Renderer, Text, TextStyle } from 'pixi.js';

import { CTAService } from 'app/modules/social/services/cta/cta.service';
import CTARoomItem from 'app/modules/social/social-pixi-canvas/cta/cta-item';
import { LocationHelperService } from 'app/modules/social/social-pixi-canvas/location-helper.service';
import CTAItem from 'entity/CTAItem';
import ButtonSize from 'enums/ButtonSize';

export class EditableItem extends CTARoomItem {
  public ctaItem: CTAItem;
  private border!: Graphics;
  private saveButtonContainer!: Container;
  public lastX: number;
  public lastY: number;
  private labelTextContainer!: Container;
  private cancelButtonContainer!: Container;

  // resizing logic
  private dragging = false;
  private startX = 0;
  private startWidth = 0;
  private startHeight = 0;
  private aspectRatio = 0;
  private rendererSize: number;

  public constructor(
    renderer: Renderer,
    item: CTAItem,
    private ctaService: CTAService,
    private _toasterService: ToastrService,
    private locationHelperService: LocationHelperService,
  ) {
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    super(renderer, { item, ratio: 1, pointerdown: () => {}, compressed: true });
    this.ctaItem = item;
    this.name = item.CTAItemId?.toString() ?? '';
    this.rendererSize = renderer.height;
    const position = this.locationHelperService.getLocalCoordinates(
      this.ctaItem.x ?? 0,
      this.ctaItem.y ?? 0,
      1000,
      this.rendererSize,
    );

    this.position.set(position.x, position.y);

    this.lastX = this.x;
    this.lastY = this.y;
    return this;
  }

  public async init() {
    await this.load(this.ctaItem, 1, true);

    this.border = new Graphics();
    this.border.zIndex = 1;
    this.border.name = 'border';
    this.border.visible = false;
    // TODO: enable resize
    // this.enableResize();

    const bounds = this.getBounds();
    const padding = 50;
    const w = bounds.width;
    const h = bounds.height;

    const buttonInfo = this.buttonSizeExample.get(this.ctaItem.buttonSize ?? ButtonSize.SMALL);
    const buttonHeight = (buttonInfo?.height ?? 0) + 20;

    this.border
      .drawRect(-((w + padding) / 2), -((h + padding) / 2), w + padding, h + padding + buttonHeight)
      .lineStyle(3, 0x4672da)
      .drawRoundedRect(-(w / 2), -(h / 2), w, h, 25);

    this.addChild(this.border);

    // TODO: use const style in new Text definition
    // const style = new TextStyle({
    //   fontFamily: 'Europa-Medium',
    //   fontSize: 12,
    //   fill: 0xffffff,
    //   align: 'center',
    //   letterSpacing: 0.25,
    //   fontWeight: '400',
    // });

    // TODO: improve center position of text to be more responsive
    // define first to determine length of text
    const limit = 10;
    const formattedText =
      this.ctaItem.name && this.ctaItem.name.length < limit
        ? this.ctaItem.name
        : this.ctaItem.name?.slice(0, limit) + '...';
    const labelText = new Text(
      formattedText,
      new TextStyle({
        fontFamily: 'Europa-Medium',
        fontSize: 12,
        fill: 0xffffff,
        align: 'center',
        letterSpacing: 0.25,
        fontWeight: '400',
      }),
    );
    labelText.name = 'label';
    labelText.eventMode = 'none';
    labelText.anchor.set(0, 0.5);
    labelText.x = 15;
    labelText.y = 13;
    labelText.zIndex = 3;

    this.labelTextContainer = new Container();
    this.labelTextContainer.name = 'label-text-container';
    this.labelTextContainer.eventMode = 'none';
    this.labelTextContainer.sortableChildren = true;
    this.labelTextContainer.visible = false;
    this.labelTextContainer.zIndex = 3;
    this.labelTextContainer.cursor = 'pointer';
    this.labelTextContainer.x = -w / 2;
    this.labelTextContainer.y = -h / 2 - 50;

    // Format text to match background
    // const textWidth = Math.floor(TextMetrics.measureText(this.ctaItem.name ?? '', style).width);
    // const maxLength = Math.floor(textWidth / 10);

    // const labelTextWidth = labelText._bounds.maxX + 40;
    const labelTextWidth = labelText.width + 40;

    // if (textWidth > labelTextWidth) {
    //   const index = maxLength - 1;

    //   const t = this.ctaItem.name ? this.ctaItem.name.slice(0, index) : '';
    //   formattedText = t.concat('...');
    // }

    labelText.text = formattedText ?? '';

    this.addChild(this.labelTextContainer);
    this.labelTextContainer.addChild(labelText);

    const labelTextBackground = new Graphics();
    labelTextBackground.zIndex = 2;
    labelTextBackground.name = 'label-text-background';
    labelTextBackground.beginFill(0x4672da).drawRoundedRect(-10, 0, labelTextWidth, 25, 15).endFill();
    this.labelTextContainer.addChild(labelTextBackground);

    const labelTextBackgroundSharpedges = new Graphics();
    labelTextBackgroundSharpedges.zIndex = 2;
    labelTextBackgroundSharpedges.name = 'label-text-background-sharpedges';
    labelTextBackgroundSharpedges.beginFill(0x4672da).drawRect(-10, 15, labelTextWidth, 10).endFill();
    this.labelTextContainer.addChild(labelTextBackgroundSharpedges);

    const bottomButtonsY = -13;
    const bottomButtonsTextX = 23;
    const bottomButtonsTextY = 5;

    this.cancelButtonContainer = new Container();
    this.cancelButtonContainer.zIndex = 3;
    this.cancelButtonContainer.name = 'cancel-button-container';
    this.cancelButtonContainer.eventMode = 'static';
    this.cancelButtonContainer.visible = false;
    this.cancelButtonContainer.cursor = 'pointer';
    this.cancelButtonContainer.x = w / 2 - 170;
    this.cancelButtonContainer.y = h / 2 + 40 + buttonHeight;
    this.cancelButtonContainer.on('pointerdown', this.cancelMove.bind(this));
    this.addChild(this.cancelButtonContainer);

    const cancelButtonBackground = new Graphics();
    cancelButtonBackground.zIndex = 2;
    cancelButtonBackground.name = 'cancelButtonBackground';
    cancelButtonBackground.beginFill(0xffffff).drawRoundedRect(-10, bottomButtonsY, 66, 36, 15).endFill();
    this.cancelButtonContainer.addChild(cancelButtonBackground);

    const cancelButtonSharpedges = new Graphics();
    cancelButtonSharpedges.zIndex = 2;
    cancelButtonSharpedges.name = 'cancelButtonSharpedges';
    cancelButtonSharpedges.beginFill(0xffffff).drawRect(-10, bottomButtonsY, 66, 15).endFill();
    this.cancelButtonContainer.addChild(cancelButtonSharpedges);

    const cancelButtonText = new Text(
      'Cancel',
      new TextStyle({
        fontFamily: 'Europa-Medium',
        fontSize: 15,
        fill: 'black',
        align: 'center',
        letterSpacing: 0.25,
        fontWeight: '400',
      }),
    );
    cancelButtonText.name = 'cancel';
    cancelButtonText.anchor.set(0.5);
    cancelButtonText.x = bottomButtonsTextX;
    cancelButtonText.y = bottomButtonsTextY;
    cancelButtonText.zIndex = 3;
    this.cancelButtonContainer.addChild(cancelButtonText);

    this.saveButtonContainer = new Container();
    this.saveButtonContainer.zIndex = 3;
    this.saveButtonContainer.name = 'saveButtonContainer';
    this.saveButtonContainer.eventMode = 'static';
    this.saveButtonContainer.visible = false;
    this.saveButtonContainer.cursor = 'pointer';
    this.saveButtonContainer.x = w / 2 - 70;
    this.saveButtonContainer.y = h / 2 + 40 + buttonHeight;
    this.saveButtonContainer.on('pointerdown', this.saveLocation.bind(this));
    this.addChild(this.saveButtonContainer);

    const saveButtonBackground = new Graphics();
    saveButtonBackground.zIndex = 2;
    saveButtonBackground.name = 'saveButtonBackground';
    saveButtonBackground.beginFill(0x4672da).drawRoundedRect(-10, bottomButtonsY, 70, 36, 15).endFill();
    this.saveButtonContainer.addChild(saveButtonBackground);

    const saveButtonSharpedges = new Graphics();
    saveButtonSharpedges.zIndex = 2;
    saveButtonSharpedges.name = 'saveButtonSharpedges';
    this.saveButtonContainer.addChild(saveButtonSharpedges);

    const saveButtonText = new Text(
      'Update',
      new TextStyle({
        fontFamily: 'Europa-Medium',
        fontSize: 15,
        fill: 0xffffff,
        align: 'center',
        letterSpacing: 0.25,
        fontWeight: '400',
      }),
    );
    saveButtonText.name = 'save';
    saveButtonText.anchor.set(0.5);
    saveButtonText.x = bottomButtonsTextX;
    saveButtonText.y = bottomButtonsTextY;
    saveButtonText.zIndex = 3;
    this.saveButtonContainer.addChild(saveButtonText);

    // make draggable
    this.eventMode = 'static';
    this.cursor = 'pointer';

    return this;
  }

  public buttonVisibility(visble: boolean) {
    this.cancelButtonContainer.visible = visble;
    this.saveButtonContainer.visible = visble;
  }

  private enableResize() {
    this.border.eventMode = 'static';
    this.border.cursor = 'nesw-resize';

    this.border.on('pointerdown', (event) => {
      if (event.target !== this.border) {
        return;
      }

      event.stopPropagation();
      this.dragging = true;
      this.startX = event.global.x;
      this.startWidth = this.width;
      this.startHeight = this.height;
      // calculate aspect ratio
      this.aspectRatio = this.startWidth / this.startHeight;
    });

    this.border
      .on('pointerup', () => {
        this.dragging = false;
      })
      .on('pointerupoutside', () => {
        this.dragging = false;
      });

    // TODO: move this listener to the scene, losing the mousemove event when dragging outside the item
    this.on(
      'pointermove',
      _.throttle((event) => {
        if (!this.dragging) {
          return;
        }
        const dx = event.global.x - this.startX;
        this.width = this.startWidth + dx;
        // maintain aspect ratio
        this.height = (this.startWidth + dx) / this.aspectRatio;
      }, 100),
    );
  }

  public select() {
    this.border.visible = true;
    this.labelTextContainer.visible = true;
  }

  public deselect() {
    this.border.visible = false;
    this.labelTextContainer.visible = false;
    this.buttonVisibility(false);
    if (this.ctaItem.x !== this.x || this.ctaItem.y !== this.y) {
      this.cancelMove(new Event(''));
    }
  }

  private saveLocation(e: Event) {
    e.stopPropagation();
    this.ctaItem.x = Math.floor(this.x);
    this.ctaItem.y = Math.floor(this.y);
    this.buttonVisibility(false);

    const position = this.locationHelperService.getWorldCoordinates(
      this.ctaItem.x,
      this.ctaItem.y,
      1000,
      this.rendererSize,
    );

    this.ctaService
      .updateCTA({ ...this.ctaItem, x: Math.floor(position.x), y: Math.floor(position.y) })
      .then(() => {
        this._toasterService.success('Item location successfully updated!', 'Success');
      })
      .catch((err) => {
        this._toasterService.error('Error when updating item location.');
        throw Error('Error when updating item location, ' + err);
      });
  }

  public cancelMove(e: Event) {
    e.stopPropagation();
    const position = this.locationHelperService.getLocalCoordinates(
      this.ctaItem.x ?? 0,
      this.ctaItem.y ?? 0,
      1000,
      this.rendererSize,
    );
    this.x = position.x;
    this.y = position.y;
    this.buttonVisibility(false);
  }

  private generateButtonY(y: number, imageHeight: number, buttonHeight: number): number {
    return y + imageHeight / 2 + buttonHeight / 2 + 20;
  }
}
